/**
 * HealthDetailModal — Platform Health Breakdown
 * Phase 4.7.5 (WQ-179)
 *
 * Shows all health components with scores, weights, and details
 * when clicking the Platform Health KPI tile.
 */

import React from 'react';
import type { PlatformHealthScore } from '../../features/dashboard/types';

interface HealthDetailModalProps {
  health: PlatformHealthScore;
  open: boolean;
  onClose: () => void;
}

const COMPONENT_LABELS: Record<string, string> = {
  tenantHealth: 'Tenant Health',
  infrastructureHealth: 'Infrastructure Health',
  containerHealth: 'Container Health',
  apiPerformance: 'API Performance',
  errorRate: 'Error Rate',
  complianceScore: 'Laws Compliance',
  sqaConfidence: 'SQA Confidence',
  operationalHealth: 'Operational Health',
};

function scoreColor(score: number): string {
  if (score >= 85) return '#22c55e';
  if (score >= 70) return '#eab308';
  return '#dc2626';
}

export const HealthDetailModal: React.FC<HealthDetailModalProps> = ({ health, open, onClose }) => {
  if (!open) return null;

  // Sort by weight descending
  const entries = Object.entries(health.components)
    .sort(([, a], [, b]) => b.weight - a.weight);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-primary, #1a1a2e)',
          border: '1px solid var(--glass-border, rgba(255,255,255,0.1))',
          borderRadius: 'var(--radius-xl, 16px)',
          padding: '24px',
          maxWidth: '560px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary)' }}>
              Platform Health Breakdown
            </h3>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Overall: <strong style={{ color: scoreColor(health.overall) }}>{health.overall}</strong>/100
              {' '}&middot;{' '}Grade <strong>{health.grade}</strong>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            &times;
          </button>
        </div>

        {/* Component rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {entries.map(([key, comp]) => (
            <div
              key={key}
              style={{
                padding: '12px',
                background: 'var(--glass-bg, rgba(255,255,255,0.03))',
                borderRadius: 'var(--radius-lg, 12px)',
                border: '1px solid var(--glass-border, rgba(255,255,255,0.06))',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {COMPONENT_LABELS[key] || key}
                  </span>
                  <span style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: 'var(--accent-primary-muted, rgba(59,130,246,0.1))',
                    color: 'var(--accent-primary, #3b82f6)',
                  }}>
                    {Math.round(comp.weight * 100)}%
                  </span>
                </div>
                <span style={{ fontSize: '14px', fontWeight: 700, color: scoreColor(comp.score) }}>
                  {comp.score}
                </span>
              </div>
              {/* Score bar */}
              <div style={{
                height: '6px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '3px',
                overflow: 'hidden',
                marginBottom: '6px',
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(100, comp.score)}%`,
                  background: scoreColor(comp.score),
                  borderRadius: '3px',
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                {comp.details}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ marginTop: '16px', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center' }}>
          Last updated: {new Date(health.timestamp).toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default HealthDetailModal;
